/* eslint-disable max-lines */
/**
 * xux edit grid widget
 */
import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { XuxGridComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';
import {
  CellFocusedEvent,
  CellPosition,
  Column,
  GridOptions,
  NavigateToNextCellParams,
  RowDataTransaction,
  RowDragEvent,
  RowNode,
  SuppressKeyboardEventParams,
  TabToNextCellParams,
} from 'ag-grid-community';
import {
  chain,
  cloneDeep,
  compact,
  concat,
  debounce,
  difference,
  every,
  filter,
  find,
  findIndex,
  forEach,
  get,
  gt,
  has,
  head,
  includes,
  invoke,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  isNumber,
  isPlainObject,
  isUndefined,
  keys,
  last,
  map,
  max,
  merge,
  omit,
  partition,
  pick,
  pullAt,
  reject,
  remove,
  reverse,
  set,
  some,
  sortBy,
  tail,
  uniq,
} from 'lodash';
import {
  BehaviorSubject,
  isObservable,
  Observable,
  observableOf,
  rx,
  Subscription,
} from '@shared/rx';
import {
  COLUMN_SELECTOR_ID,
  DEFAULT_FORM_UPDATE_DELAY_MS,
  DEFAULT_GRID_HEIGHT,
  DEFAULT_MINIMUM_GRID_HEIGHT,
  DEFAULT_TEXT_FIELD,
  ENTITY_ID_PATH,
  GRID_NAVIGATION_AVAILABLE_KEYS,
  KEY_CODES,
  ROW_SELECTOR_ID,
  UI_DIMENSIONS,
} from '@config/index';
import {
  Async,
  CUSTOM_XUX_GRID_ACTIONS,
  CustomXuxGridAction,
  DEFAULT_SORTING_ORDERS,
  EDIT_GRID_ITEM_ACTION_TYPE,
  FilterModel,
  GRID_ROW_SELECTION,
  GridRowHeightName,
  GridView,
  IBaseEntityType,
  IFieldPreferences,
  IModalDialogOptions,
  IModalDialogResult,
  IWidgetData,
  IXVIListColumn,
  ModalAction,
  REFRESH_CELLS_CONDITIONS,
  RefreshCellCondition,
  SchemaNode,
  TOOLBAR_BUTTON_POSITION,
  ToolbarButtonDefinition,
  GridRowClass,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  XuxGridOptions,
  GridValidatorFn,
} from '@shared/types';
import {
  asObservable,
  flatTreeDataByHasChildren,
  generateId,
  getFlowMethods,
  hasMethod,
  isNotClickGridCell,
  isTreeDataStructure,
  isValidNumber,
  prepareControlsInsideDataForCloning,
  toDate,
  toMoment,
  XuxGridActionButtonsHelper,
} from '@shared/utils';
import { UiActions } from '@store/actions';
import { BUTTON_TEMPLATES } from '@app/text-content';
import { moveItemsHelper } from './xux-edit-grid-widget.helpers';
import { BUTTON_ACTIONS } from '@text-content/button-templates';
import {
  clearFocusedCellRenderer,
  getCellRendererInstance,
  getDisplayedItemsIds,
} from '../../xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import {
  handleGridOverlay,
  updateGridSortFilter,
} from '@app/shared/helpers/ag-grid-helpers/ag-grid-helpers';

import { xviFrameworkComponents } from '../../xvi-grid/cell-helpers/index';
import {
  xviCustomGridComponents,
  xviKeyboardAutoFocusGridComponents,
} from '../../xvi-grid/custom-components';
import {
  INTERNAL_GRID_ROW_ID,
  INTERNAL_GRID_VALUE_SET,
} from './xux-edit-grid-widget.component.constants';
import { ITreeData } from '@shared/types/common/tree.type';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
import { AgGridService } from '@shared/services/ag-grid';
import { XuxGridActionPayload } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-action.type';
import { endOfDay } from '@app/containers/base/list/xux-list.column-routines';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';
import { I18n } from '@app/i18n/strings-keys';
import { WIDGETS_STATIC_TEXT_CONTENT } from '@app/i18n/constants/static-text-content.constants';
import { COMMON_NAME_CONFIG } from '@app/shared/configs/common-name-config';
import { COMMON_WARN_MESSAGE_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import { ILabelBlockItem } from '@shared/types/common/label.type';

const frameworkComponents = {};
xviFrameworkComponents.forEach(
  (element) => (frameworkComponents[element.key] = element),
);

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-edit-grid',
  templateUrl: './xux-edit-grid-widget.component.html',
  styleUrls: ['./xux-edit-grid-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxEditGridComponent implements OnInit, OnDestroy {
  /**
   * get visible item count description(showing xxxx item(s))
   *
   * @returns visible item count description
   */
  public get visibleItemCountDescriptionObs(): Observable<string> {
    const defaultItemsLabel = this.i18nService.instant(
      I18n.Forms.GridMessages.Items,
    );

    const itemsLabel = get(
      this.field,
      'widgetData.itemsLabel',
      defaultItemsLabel,
    );

    return this.visibleItemCount$.pipe(
      rx.map((totalGridItemCount: number) =>
        this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
          count: totalGridItemCount ?? 0,
          entities: this.i18nService.t(
            isEmpty(itemsLabel) ? defaultItemsLabel : itemsLabel,
          ),
        }),
      ),
    );
  }

  private get isMaxItemsCountReached(): boolean {
    const maxItems = this.field.maxItems;

    return isNumber(maxItems) && this.getControlValue().length >= maxItems;
  }

  @Input() public field: IFieldPreferences;
  @ViewChild(XuxGridComponent, { static: true })
  public xuxGridComponent: XuxGridComponent;

  public I18n = I18n;
  public staticI18nTextContent = this.i18nService.translateStaticTextContent({
    xuxGrid: WIDGETS_STATIC_TEXT_CONTENT.xuxGrid,
  });

  public gridTitleObs: Async<string>;
  public descriptionObs: Async<string | TemplateRef<any>>;
  public gridTitleTipsObs: Async<string>;

  public disabledInlineAddBtn$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public visibleItemCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  public selectedItemCount$: BehaviorSubject<number> = new BehaviorSubject(0);

  public widgetData: IWidgetData = {};
  public disabled: boolean;
  public rowSelectableWhenDisabled: boolean;
  public subscriptions: Subscription[] = [];
  public ignoreItemValueOptimization: boolean;
  public showPristineErrors: boolean = true;
  public filterBeforeUpdateItems: boolean = false;
  /**
   * will refresh whole grid cell when target condition field change
   */
  public fieldChangeRefreshCellCondition: RefreshCellCondition = {};

  public topLabels: Observable<ILabelBlockItem[]> = observableOf([]);
  public warningLabels: ILabelBlockItem[] = [];
  private warningState: ValidationErrors | null = null;
  // Track form-control validation errors separately from warning validators.
  private errorState: ValidationErrors | null = null;
  // Remember the wrapper function installed for each gridOptions instance.
  private validationGetRowClassByGridOptions: WeakMap<
    object,
    (params: any) => string | string[]
  > = new WeakMap();

  // properties for grid
  public gridOptions: XuxGridOptions = {
    orderControls: true,
    multiselect: true,
    animateRows: false,
    showSearch: false,
    showRowOptions: false,
    showViewSelector: false,
    showPagination: false,
    valueField: ENTITY_ID_PATH,
    orderGhostLabelPath: DEFAULT_TEXT_FIELD,
    disableColumnSelector: false,
    hideActionPanel: false,
    gridRowHeight: GridRowHeightName.MEDIUM,
    gridHeight: DEFAULT_GRID_HEIGHT,
    gridMinHeight: DEFAULT_MINIMUM_GRID_HEIGHT,
    treeViewMode: false,
    treeChildrenPropertyName: undefined,
    inlineRemove: false,
    inlineRemoveParams: null,
    inlineAdd: false,
    inlineAddButtonCaption: null,
    disabledInlineAddObs: null,
    defaultDocument: null,
    actionAcceptanceValidators: [],
    rowSelection: GRID_ROW_SELECTION.MULTIPLE,
    defaultExpanded: false,
    searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
    useLegacyAutoHeightCalculation: false,
    isLeafRow: null,
    alwaysFindNextEditCell: false,
    focusCellInnerElWithDebounce: false,
  };

  public userGridOptions: GridOptions = {
    stopEditingWhenGridLosesFocus: false,
    enableServerSideFilter: false,
    scrollbarWidth: UI_DIMENSIONS.defaultGridScrollbarWidth,
    onRowDragMove: this.onRowDragMove.bind(this),
    onRowDragEnd: this.onRowDragEnd.bind(this),
    onCellFocused: this.onCellFocused.bind(this),
    onCellMouseDown: this.onCellMouseDown.bind(this),
    onCellEditingStopped: this.onCellEditingStopped.bind(this),
    navigateToNextCell: this.navigateToNextEditableCell.bind(this),
    tabToNextCell: this.tabToNextEditableCell.bind(this),
  };

  public availableActionButtons: ToolbarButtonDefinition[] = [];
  public actionButtons: ToolbarButtonDefinition[] = [];
  public gridButtons: ToolbarButtonDefinition[] = [];
  public rowButtons: ToolbarButtonDefinition[] = [];
  public headerButtons: ToolbarButtonDefinition[] = [];
  public initXuxGridItems: object[] = [];
  public xuxGridItems: object[] = [];
  public displayedItemsIds: string[] = [];
  public needToAddUniqIdentifier: boolean = false;

  public gridView: GridView = {};
  public isGridRowLoader$: Observable<boolean>;

  public gridColumns: IXVIListColumn[];

  // this property store index of item on which action is performed
  public currentItemIndex: number = -1;
  public dmCustomGridComponents: object = Object.assign(
    {},
    frameworkComponents,
    xviCustomGridComponents,
  );
  public showPinnedRow: boolean = false;
  public pinnedTopRowData: any[] = [];
  public advancedOrderControls: boolean = false;
  public modalTitlePrefixes: { create: ModalAction; edit: ModalAction };
  /**
   * grid last filter search string
   */
  public lastSearchString: string;

  protected debounceFocusCellInnerElement = debounce(
    (event) => this.focusCellInnerElement(event),
    10,
  );

  private modalOptions: IModalDialogOptions = {
    title: I18n.Common.Create,
    schema: {},
    formRules: {},
    value: null,
    validation: true,
  };
  private schemaSubscription: Subscription;

  public constructor(
    public uiActions: UiActions,
    public agGridService: AgGridService,
    public i18nService: XVII18nService,
  ) {}

  @HostListener('document:click', ['$event'])
  public removeCellFocusClass(event: MouseEvent): void {
    if (isNotClickGridCell(event)) {
      const gridApi =
        this.agGridService?.focusedGridContext?.getBean('gridApi');
      clearFocusedCellRenderer({ gridApi });
      gridApi?.clearFocusedCell();
    }
  }

  /**
   * update inlineRemove data by gridColumns
   * is gridColumns empty, will set inlineRemove to false
   *
   * @param gridColumns gridColumns
   */
  public resetGridColumns(gridColumns: IXVIListColumn[]): void {
    const newInlineRemove = isEmpty(gridColumns)
      ? false
      : get(this.widgetData, 'inlineRemove', this.gridOptions.inlineRemove);

    if (newInlineRemove !== this.gridOptions.inlineRemove) {
      this.gridOptions = {
        ...this.gridOptions,
        inlineRemove: newInlineRemove,
      };
    }
  }

  public handleGridColumns(): void {
    if (!isNil(this.widgetData?.columns)) {
      this.gridColumns = this.i18nService.translateTextInColumns(
        map(this.widgetData?.columns, (column: IXVIListColumn) => {
          if (isNil(column.editable)) {
            column.editable = false;
          }
          if (column.editable) {
            column.suppressKeyboardEvent = this.suppressKeyboardEvent;
          }

          return column;
        }),
      );
    }

    this.subscriptions.push(
      this.widgetData?.columnsObs?.subscribe((newColumns) => {
        const translatedColumns =
          this.i18nService.translateTextInColumns(newColumns);
        // update grid columns
        this.gridColumns = translatedColumns;
        this.resetGridColumns(translatedColumns);
      }),
    );
  }

  public handleInlineRemove(): void {
    this.subscriptions.push(
      this.widgetData?.inlineRemoveObs?.subscribe((newInlineRemove) => {
        this.widgetData.inlineRemove = newInlineRemove;
        this.gridOptions.inlineRemove = newInlineRemove;
      }),
    );
  }

  public handleDisableObs(): void {
    const disabledObs: Observable<boolean> = get(this, 'field.disabledObs');

    if (disabledObs) {
      this.subscriptions.push(
        disabledObs.subscribe((isDisabled: boolean) => {
          this.disabled = isDisabled;
        }),
      );
    }
  }

  public ngOnInit(): void {
    this.widgetData = this.getWidgetDataDefaults();
    this.topLabels = asObservable(get(this.widgetData, 'topLabels', []));
    this.fieldChangeRefreshCellCondition = get(
      this.widgetData,
      'fieldChangeRefreshCellCondition',
      {},
    );
    this.showPristineErrors = get(this.widgetData, 'showPristineErrors', true);
    this.filterBeforeUpdateItems = get(
      this.widgetData,
      'filterBeforeUpdateItems',
      this.filterBeforeUpdateItems,
    );
    this.updateSchemaSubscription(
      asObservable(get(this.widgetData, 'rowSchema')),
    );
    this.modalOptions.formRules = get(this.widgetData, 'rowRules', {});
    this.modalOptions.modalMappers = get(
      this.widgetData,
      'modalOptions.modalMappers',
      {},
    );
    this.modalOptions.onFormReady = get(
      this.widgetData,
      'onModalFormReady',
      null,
    );
    this.modalOptions.onFormAction = get(this.widgetData, 'onFormAction', null);
    this.modalOptions.onFormClose = get(
      this.widgetData,
      'onModalFormClose',
      null,
    );
    this.modalOptions.modalClass = get(this.widgetData, 'modalClass', '');
    this.modalOptions.defaultWidgetFamily = get(
      this.widgetData,
      'defaultWidgetFamily',
      '',
    );
    this.modalOptions.schemaMapper = get(this.widgetData, 'schemaMapper');
    this.modalOptions.entityType = get(this.widgetData, 'entityType');
    this.modalOptions.setupEmitWidgetEvent = get(
      this.widgetData,
      'modalOptions.setupEmitWidgetEvent',
    );
    this.isGridRowLoader$ = get(this.widgetData, 'isGridRowLoader$');
    this.visibleItemCount$ = get(
      this.widgetData,
      'displayCountObs',
      this.visibleItemCount$,
    );
    this.selectedItemCount$ = get(
      this.widgetData,
      'selectedCountObs',
      this.selectedItemCount$,
    );
    this.rowSelectableWhenDisabled = get(
      this.widgetData,
      'rowSelectableWhenDisabled',
      false,
    );
    this.ignoreItemValueOptimization = get(
      this.widgetData,
      'ignoreItemValueOptimization',
      false,
    );

    // prevent tab event
    this.preventTabEvent();

    // handle grid columns
    this.handleGridColumns();

    // handle inlineRemoveObs
    this.handleInlineRemove();

    const buttons: ToolbarButtonDefinition[] | null = get(
      this.widgetData,
      'modalOptions.buttons',
      null,
    );
    if (buttons) {
      this.modalOptions.buttons = buttons;
    }

    this.showPinnedRow = get(this.widgetData, 'showPinnedRow', false);
    this.advancedOrderControls = get(
      this.widgetData,
      'advancedOrderControls',
      false,
    );

    // do not fill pinnedTopRowData when its hidden
    if (this.showPinnedRow) {
      this.pinnedTopRowData = get(this.widgetData, 'pinnedTopRowData', []);
    }

    this.handleDisableObs();

    merge(
      this.userGridOptions,
      { sortingOrder: DEFAULT_SORTING_ORDERS },
      this.widgetData.userGridOptions,
    );
    // pick all gridOptions values from widget data when available
    const gridHeightObs = get(this.widgetData, 'gridHeightObs');
    const gridOptionsKeys = Object.keys(this.gridOptions);
    if (!isNil(gridHeightObs) && isObservable(gridHeightObs)) {
      gridOptionsKeys.push('gridHeightObs');
    }

    this.gridOptions = Object.assign(
      {},
      this.gridOptions,
      pick(this.widgetData, gridOptionsKeys),
    );

    this.resetGridColumns(this.gridColumns);
    this.prepareActionButtons();
    this.updateActionButtons([]);

    if (!this.field?.control?.value && has(this.widgetData, 'defaultValue')) {
      this.setFieldControlValue(this.widgetData.defaultValue);
    }
    const initialValue = get(this.field, 'control.value', []);
    const isValueFieldPresent = !!this.widgetData.valueField;

    if (!isValueFieldPresent) {
      this.gridOptions.valueField = INTERNAL_GRID_ROW_ID;
    }
    this.needToAddUniqIdentifier = !isValueFieldPresent;

    this.warningLabels = this.buildWarningLabels(this.warningState);

    this.updateGridState(initialValue);

    this.initEditGridWidgetSubscription();

    this.gridTitleObs = get(
      this.field,
      'titleObs',
      observableOf(this.field.title),
    );
    this.gridTitleTipsObs = get(
      this.field,
      'titleTipsObs',
      observableOf(this.field.titleTips),
    );
    const description = get(this.field, 'description');
    const useVisibleItemCountDescription = get(
      this.field,
      'widgetData.useVisibleItemCountDescription',
      false,
    );
    // useVisibleItemCountDescription will override description
    this.descriptionObs = useVisibleItemCountDescription
      ? this.visibleItemCountDescriptionObs
      : get(this.field, 'descriptionObs', observableOf(description));
    this.modalTitlePrefixes = Object.assign(
      {},
      {
        create: 'create',
        edit: 'edit',
      },
      get(this.widgetData, 'modalTitlePrefix', {}),
    );
  } // end ngOnInit()

  public ngOnDestroy(): void {
    this.restoreTabEvent();
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
  }

  public updateGridItems(
    items: object[],
    isForceUpdateInitGridItems: boolean = false,
  ): void {
    const newItems = [...items];
    this.xuxGridItems = this.needToAddUniqIdentifier
      ? this.addUniqIdentifierSafety(newItems)
      : newItems;
    this.updateInnerXuxGridItems(this.xuxGridItems);

    // if isForceUpdateInitGridItems
    // or is not from internal grid value set, need reset whole grid data
    if (
      isForceUpdateInitGridItems ||
      !get(head(this.xuxGridItems), INTERNAL_GRID_VALUE_SET, false)
    ) {
      // update init items
      if (
        this.filterBeforeUpdateItems &&
        !this.userGridOptions.enableServerSideSorting &&
        !isNil(this.gridView.filterModel)
      ) {
        // re-filter by old filter model
        this.filterModelChangedClient(this.gridView.filterModel);
      } else {
        this.initXuxGridItems = this.xuxGridItems;
        this.setGridRowData(this.xuxGridItems);
      }
      // reset allCollapsed from config
      // to fix grid row will expanded all when re set all same data and expanded once line before
      this.xuxGridComponent.allCollapsed = !get(
        this.gridOptions,
        'defaultExpanded',
        false,
      );
    }

    if (has(head(newItems), INTERNAL_GRID_VALUE_SET)) {
      // remove INTERNAL_GRID_ROW_ID, INTERNAL_GRID_VALUE_SET field of control value, not need to emit any event
      const newValues = this.removeUniqIdentifier(
        this.removeInternalSetFlagOfHead(newItems),
      );

      this.field.control.setValue(newValues, {
        onlySelf: true,
        emitEvent: false,
        emitModelToViewChange: false,
        emitViewToModelChange: false,
      });
    }
  }

  public updateGridState(items: any[]): void {
    this.updateGridItems(items);
    this.toggleCreateButton();
    this.updateErrorState();
    this.updateWarningState();
  }

  public forceUpdateXuxGridItems(items: object[]): void {
    this.updateGridItems(items, true);
  }

  public addUniqIdentifierSafety(items: object[]): object[] {
    return map(items, (item) => {
      if (has(item, INTERNAL_GRID_ROW_ID)) {
        return item;
      }

      return {
        ...item,
        [INTERNAL_GRID_ROW_ID]: `local${generateId()}`,
      };
    });
  }

  public removeUniqIdentifier(value: object[] | object): object[] | object {
    return isArray(value)
      ? map(value, (item) => this.omitInternalRowGridId(item))
      : this.omitInternalRowGridId(value);
  }

  public omitInternalRowGridId(value: object): object {
    return this.needToAddUniqIdentifier
      ? omit(value, INTERNAL_GRID_ROW_ID)
      : value;
  }

  public getCurrentItemIndex(rowIndex: number): number {
    const selectedItemRowIndex = head(this.getSelectedItemsRowIndexes());

    return (this.currentItemIndex = isNumber(rowIndex)
      ? rowIndex
      : selectedItemRowIndex);
  }

  public updateVisibleItemCount(selectedCount?: number | null): void {
    const displayedItemsIds = getDisplayedItemsIds(
      this.xuxGridComponent.gridApi,
    );
    const isTreeViewMode = get(this.gridOptions, 'treeViewMode', false);
    const controlValue = this.getControlValue();
    // The number of list view items.
    let count: number = displayedItemsIds.length;

    // The number of three view items.
    if (isTreeViewMode && isTreeDataStructure(controlValue)) {
      const listViewData = this.getListViewData();
      const displayedItems = filter(listViewData, (item) =>
        // @ts-ignore
        includes(displayedItemsIds, item.internal_grid_row_id),
      );
      count = displayedItems.length;
    }

    if (isNumber(selectedCount)) {
      this.selectedItemCount$.next(selectedCount);
    }
    this.visibleItemCount$.next(count ?? 0);
  }

  public reFocusCell(): void {
    const focusedCell = this.xuxGridComponent.gridApi.getFocusedCell();

    if (!isEmpty(focusedCell?.rowPinned)) return;

    if (focusedCell) {
      this.xuxGridComponent.gridApi.setFocusedCell(
        focusedCell.rowIndex,
        focusedCell.column,
      );
    }
  }

  public exitEditingMode(payload: XuxGridActionPayload): void {
    const focusedCell = this.xuxGridComponent.gridApi.getFocusedCell();

    if (isNil(focusedCell) || !isEmpty(focusedCell.rowPinned)) return;

    const rowCount = this.xuxGridComponent.gridApi.getModel().getRowCount() - 1;
    const nextRowIdx = this.getNextAvailableRowIdx(
      focusedCell.rowIndex < rowCount ? focusedCell.rowIndex : rowCount,
      rowCount,
      get(this.userGridOptions, 'isDrawer', false),
    );
    if (payload.navigateToNextRow) {
      this.xuxGridComponent.gridApi.setFocusedCell(
        isNumber(nextRowIdx) ? nextRowIdx : focusedCell.rowIndex,
        focusedCell.column,
      );
    } else {
      this.reFocusCell();
    }
    if (focusedCell.rowIndex < rowCount && payload.navigateToNextRow) {
      this.onCellMouseDown();
    }
  }

  public navigateToNextCell(payload: XuxGridActionPayload): void {
    const focusedCell = this.xuxGridComponent.gridApi.getFocusedCell();

    if (isNil(focusedCell)) return;

    const rowCount = this.xuxGridComponent.gridApi.getModel().getRowCount() - 1;
    const allColumns = this.xuxGridComponent.columnApi.getColumnState();

    const isJumpToEditable: boolean = isEqual(
      focusedCell.column.getColId(),
      'fieldData',
    );
    switch (payload.direction) {
      case KEY_CODES.ARROW_UP:
        const preIndex: number = this.getPreviousAvailableRowIdx(
          focusedCell.rowIndex,
          isJumpToEditable,
        );
        if (!isNil(preIndex)) {
          this.xuxGridComponent.gridApi.setFocusedCell(
            preIndex,
            focusedCell.column,
          );
        }

        break;
      case KEY_CODES.ARROW_DOWN:
        const nextIndex: number = this.getNextAvailableRowIdx(
          focusedCell.rowIndex,
          rowCount,
          isJumpToEditable,
        );

        if (!isNil(nextIndex)) {
          this.xuxGridComponent.gridApi.setFocusedCell(
            nextIndex,
            focusedCell.column,
          );
        }
        break;
      case KEY_CODES.ARROW_LEFT:
        allColumns.reverse();
        this.xuxGridComponent.gridApi.setFocusedCell(
          focusedCell.rowIndex,
          this.getNextEditableColumn(allColumns, focusedCell),
        );
        break;
      case KEY_CODES.ARROW_RIGHT:
        this.xuxGridComponent.gridApi.setFocusedCell(
          focusedCell.rowIndex,
          this.getNextEditableColumn(allColumns, focusedCell),
        );
        break;
      default:
        break;
    }

    this.onCellMouseDown();
  }

  public tabToPreviousCell(): void {
    this.xuxGridComponent.gridApi.tabToPreviousCell();
    this.onCellMouseDown();
  }

  public tabToNextCell(): void {
    this.xuxGridComponent.gridApi.tabToNextCell();
    this.onCellMouseDown();
  }

  // eslint-disable-next-line complexity
  public async onGridAction($event: CustomXuxGridAction): Promise<void> {
    const actionType: XUX_GRID_ACTIONS | CUSTOM_XUX_GRID_ACTIONS = get(
      $event,
      'type',
      null,
    );
    const payload = $event.payload;
    this.currentItemIndex = this.getCurrentItemIndex(payload.rowIndex);

    // invoke additional handlers from widgetData if present - add ability to control grid from formRules
    invoke(this.widgetData, ['eventHandlers', actionType], $event, this);
    const asyncEventHandler = get(this.widgetData, 'asyncEventPreHandler');
    if (asyncEventHandler) {
      await asyncEventHandler($event, this);
    }

    switch (actionType) {
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        const filterModel = get($event, 'payload.filterModel', {});
        if (!isEqual(filterModel, this.gridView.filterModel)) {
          this.gridView = {
            ...this.gridView,
            filterModel,
          };
        }
        if (!this.userGridOptions.enableServerSideSorting) {
          this.filterModelChangedClient(payload.filterModel);
        }
        break;
      case XUX_GRID_ACTIONS.SORT_MODEL_CHANGED:
        updateGridSortFilter(this.xuxGridComponent, this.lastSearchString);
        const sortModel = get($event, 'payload.sortModel', {});
        if (!isEqual(sortModel, this.gridView.sortModel)) {
          this.gridView = {
            ...this.gridView,
            sortModel: get($event, 'payload.sortModel', []),
          };
        }
        break;
      case XUX_GRID_ACTIONS.GRID_READY:
        this.updateVisibleItemCount(0);
        // reset filter string when grid ready
        this.lastSearchString = '';
        this.refreshPinnedRowCellRenderer();
        this.installValidationGetRowClass();
        // Sync any warning/error state that was computed before the grid was
        // ready (ngOnInit runs updateWarningState before GRID_READY fires, so
        // the first syncErrorAndWarningToGridApi call exits early due to missing
        // gridApi). Re-sync now that the grid is ready.
        this.syncErrorAndWarningToGridApi();

        const context = get(payload, [
          'gridApi',
          'gridOptionsWrapper',
          'gridOptions',
          'context',
        ]);

        if (context) {
          // update context
          set(
            context,
            'isTreeViewMode',
            this.xuxGridComponent?.['isTreeViewMode']?.(),
          );
        }
        break;
      case XUX_GRID_ACTIONS.ITEM_ORDER_CHANGED:
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        this.widgetData.allowMovingNestedItems
          ? this.setControlValue(this.reorderItems(payload))
          : this.setControlValue(payload.rowData);
        break;
      case XUX_GRID_ACTIONS.ITEM_DATA_CHANGED:
        this.inlineCellEditComplete(payload);
        this.judgeExitEditMode(payload);
        break;
      case XUX_GRID_ACTIONS.PINNED_TOP_ITEM_DATA_CHANGED:
        this.inlinePinnedTopCellEditComplete(payload);
        break;
      case XUX_GRID_ACTIONS.ITEMS_DATA_CHANGED:
        // this event mostly used by default grid editors
        this.setControlValue(payload.rowData);
        break;
      case XUX_GRID_ACTIONS.ROW_DATA_CHANGED:
        this.inlineRowEditComplete(payload);
        break;
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.updateVisibleItemCount(payload?.rowData?.length || 0);
        this.updateActionButtons(payload.rowData);
        break;

      case XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED:
        if (this.widgetData.canEdit) {
          this.editItem(head(payload.rowData), payload.rowIndex);
        }
        break;

      case XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED:
        this.handleActionButtonClicked(payload);
        XuxGridActionButtonsHelper.invokeCustomClickHandler(
          this.actionButtons,
          payload.value,
          payload.rowData,
        );
        break;

      case XUX_GRID_ACTIONS.REMOVE_ROW:
        this.removeItems(payload.rowData, payload.noConfirm);
        break;

      case XUX_GRID_ACTIONS.ADD_ROW:
        const customButtonHandler = get(this.widgetData, 'actionButtonHandler');
        if (!customButtonHandler) {
          this.insertNewItem();
        } else {
          this.customButtonHandler(customButtonHandler);
        }
        break;

      case XUX_GRID_ACTIONS.COLUMN_SELECTION_CHANGED:
        this.gridView = {
          ...this.gridView,
          selectedColumns: payload.selectedColumns,
        };
        if (!get(this.widgetData, 'disableColumnSelector', true)) {
          // let the selected columns show
          this.updateColumnVisibility(payload.selectedColumns);
        }
        break;

      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        // update filter string
        this.lastSearchString = payload.value;
        this.displayedItemsIds = handleGridOverlay(this.xuxGridComponent);
        this.updateVisibleItemCount();
        // refresh sort filter to current string
        updateGridSortFilter(this.xuxGridComponent, this.lastSearchString);
        this.xuxGridComponent.gridApi.resetRowHeights();
        break;

      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        handleGridOverlay(this.xuxGridComponent);
        this.updateVisibleItemCount();
        this.refreshPinnedRowCellRenderer();
        // Ensure initial warning/error row classes are applied after rows are
        // actually rendered into the grid model.
        this.syncErrorAndWarningToGridApi();
        this.setDisabledInlineAddBtnObs();
        break;

      case CUSTOM_XUX_GRID_ACTIONS.EXIT_EDITING_MODE:
        this.exitEditingMode(payload);
        break;

      case CUSTOM_XUX_GRID_ACTIONS.NAVIGATE_TO_NEXT_CELL:
        this.navigateToNextCell(payload);
        break;

      case CUSTOM_XUX_GRID_ACTIONS.TAB_TO_PREVIOUS_CELL:
        this.tabToPreviousCell();
        break;

      case CUSTOM_XUX_GRID_ACTIONS.TAB_TO_NEXT_CELL:
        this.tabToNextCell();
        break;

      default:
    }

    if (hasMethod(this, 'field.action.next')) {
      this.emitAction($event);
    }
  }

  /**
   * judge is need exit editing mode
   *
   * @param payload xux grid action payload
   */
  public judgeExitEditMode(payload: XuxGridActionPayload): void {
    if (has(payload, 'navigate') && !isEmpty(payload['navigate'])) {
      this.exitEditingMode(payload.navigate);
    }
  }

  public updateColumnVisibility(selectedColumnIds: string[]): void {
    if (!this.xuxGridComponent?.gridApi) {
      return;
    }

    const visibleColumnIds = compact(
      uniq(
        [].concat(
          map(
            reject(this.xuxGridComponent?.columnDefinitions, 'hide'),
            'colId',
          ),
          this.xuxGridComponent.view.selectedColumns,
          COLUMN_SELECTOR_ID,
          ROW_SELECTOR_ID,
          selectedColumnIds,
        ),
      ),
    );
    const hiddenColumns = difference(
      map(this.xuxGridComponent.columns, 'path'),
      visibleColumnIds,
    );
    const canSelectedColumns = map(
      filter(
        this.xuxGridComponent.columns,
        (item: IXVIListColumn) => item.canSelected,
      ),
      'path',
    );
    const finalHideColumns = filter(
      concat(hiddenColumns, canSelectedColumns),
      (hideId: string) => !includes(selectedColumnIds, hideId),
    );
    this.xuxGridComponent.columnApi.setColumnsVisible(visibleColumnIds, true);

    if (!isEmpty(finalHideColumns)) {
      this.xuxGridComponent.columnApi.setColumnsVisible(
        finalHideColumns,
        false,
      );
    }

    this.xuxGridComponent.gridResize();
  }

  public filterModelChangedClient(filterModel: FilterModel): void {
    const isTreeViewMode = get(this.gridOptions, 'treeViewMode', false);

    if (isTreeViewMode) {
      return;
    }

    const items = cloneDeep(this.xuxGridItems);
    const filterKeys = Object.keys(filterModel);
    const filterList = filter(items, (item: object) => {
      const conditions = map(
        filter(filterKeys, (key: string) => {
          return filterModel[key];
        }),
        (key: string) => {
          const filterType = get(filterModel[key], 'type');
          const dateFrom = get(filterModel[key], 'dateFrom', '');
          const dateTo = get(filterModel[key], 'dateTo', '');

          switch (filterType) {
            case 'equals':
              if (isEmpty(dateFrom)) {
                return true;
              }
              if (!isEmpty(dateFrom)) {
                return toMoment(item[key])
                  .utc()
                  .isBetween(dateFrom, endOfDay(dateFrom), undefined, '[]');
              }
              break;
            case 'inRangeDate':
              if (isEmpty(dateFrom) && isEmpty(dateTo)) {
                return true;
              }
              if (isEmpty(dateFrom) && !isEmpty(dateTo)) {
                return toMoment(item[key]).utc().isSameOrBefore(dateTo);
              }
              if (!isEmpty(dateFrom) && isEmpty(dateTo)) {
                return toMoment(item[key]).utc().isSameOrAfter(dateFrom);
              }

              if (!isEmpty(dateFrom) && !isEmpty(dateTo)) {
                const [startDay, endDay] = sortBy([
                  toDate(dateFrom).getTime(),
                  toDate(dateTo).getTime(),
                ]);

                return toMoment(item[key])
                  .utc()
                  .isBetween(startDay, endDay, undefined, '[]');
              }

              break;

            case 'containsAny':
              if (isEmpty(filterModel[key]?.filter)) {
                return true;
              }

              return includes(
                filterModel[key]?.filter as string[],
                get(item, key),
              );

            default:
              if (isEmpty(filterModel[key]?.filter)) {
                return true;
              }

              return includes(item[key], filterModel[key]?.filter as string[]);
          }
        },
      );

      return every(conditions, (condition: boolean) => Boolean(condition));
    });

    this.initXuxGridItems = filterList;
    this.setGridRowData(this.xuxGridItems);
  }

  public reorderItems(payload: XuxGridAction['payload']): object[] {
    const movingNode: RowNode = payload.value.node;
    const overNode: RowNode = payload.value.overNode;
    const propName: string = this.gridOptions.treeChildrenPropertyName;
    const valueField: string = this.gridOptions.valueField;
    const updatedRowData: object[] = payload.rowData.concat();

    if (!overNode || !movingNode) return updatedRowData;

    // Getting path of ids for moving node: Root Parent -> Parent (1..4) -> Child
    const pathToMovingNode: string = this.getNodePath(
      movingNode,
      valueField,
      propName,
      updatedRowData,
    );
    const pathToOverNode: string = this.getNodePath(
      overNode,
      valueField,
      propName,
      updatedRowData,
    );

    // Do nothing if paths for moving node and for over node are the same
    if (pathToMovingNode === pathToOverNode) return updatedRowData;

    // Prevent moving nodes with nested items into other nodes. Supports 3 nested levels, including zero level
    const maxUiLevel: number = 5;
    const maxLevelOfMovingItem: number =
      max(map(movingNode.allLeafChildren, 'level')) ?? 0;
    if (maxLevelOfMovingItem + overNode.level > maxUiLevel) {
      return updatedRowData;
    }

    // Removing movingNode from its parent and saving moving node to the constant
    const movingNodeIndex: number = Number(
      pathToMovingNode.split('.').slice(-1),
    );
    const movingParent: object[] =
      movingNode.parent.data?.[propName] ?? movingNode.data?.[propName];
    const pulledMovingNode = pullAt(movingParent, movingNodeIndex);

    if (isEmpty(compact(pulledMovingNode))) return updatedRowData;

    // Put saved previously node to new place
    const overNodeIndex: number = Number(pathToOverNode.split('.').slice(-1));
    const overParent: object[] =
      overNode.parent.data?.[propName] ?? overNode.data?.[propName];
    overParent.splice(overNodeIndex, 0, pulledMovingNode[0]);

    // Remove group when propName array exists and it is empty
    if (isEmpty(movingParent)) {
      const movingGrandParentPath: string = pathToMovingNode
        .split('.')
        .slice(0, -3)
        .join('.');
      const movingGrandParent: object[] = get(
        updatedRowData,
        movingGrandParentPath,
        [],
      );

      remove(
        movingGrandParent,
        (parent: object[]) =>
          isArray(parent[propName]) && isEmpty(parent[propName]),
      );
    }

    return updatedRowData;
  }

  public getNodePath(
    node: RowNode,
    valueField: string,
    propName: string,
    rowData,
  ): string {
    let prevNodeId: string = node.data?.[valueField];
    let tempParentNode: RowNode = node;
    let pathToNode: string = '';

    for (let i = node.level; i > 0; i = i - 1) {
      tempParentNode = tempParentNode.parent;
      const indexOfCurrentNode: number = findIndex(
        tempParentNode.data?.[propName],
        {
          [valueField]: prevNodeId,
        },
      );
      pathToNode = `${propName}.${indexOfCurrentNode}.${pathToNode}`;
      prevNodeId = tempParentNode.data?.[valueField];
    }
    const parentIndex: number = findIndex(rowData, {
      [valueField]: tempParentNode.data?.[valueField],
    });

    // If there is no path - create it in the first element
    return pathToNode.slice(0, -1)
      ? `${parentIndex}.${pathToNode.slice(0, -1)}`
      : `${parentIndex}.${propName}.0`;
  }

  public insertNewItem(): void {
    if (isEmpty(this.gridOptions.defaultDocument)) return;

    // use this to sync sort model clear
    this.xuxGridComponent.gridApi.setSortModel(null);

    const firstColumnPath = head(Object.keys(this.gridOptions.defaultDocument));
    const preparedItemToInsert = head(
      this.addUniqIdentifierSafety([this.gridOptions.defaultDocument]),
    );

    this.addGridRowData(preparedItemToInsert);

    setTimeout(
      () =>
        this.xuxGridComponent.gridApi.startEditingCell({
          rowIndex: this.xuxGridComponent.gridApi.getLastDisplayedRow(),
          colKey: firstColumnPath,
        }),
      0,
    );
  }

  public setValue(value: object[]): void {
    this.setFieldControlValue(value);
    this.field.control.markAsTouched();
    this.field.control.markAsDirty();
  }

  public inlineRowEditComplete(payload: XuxGridAction['payload']): void {
    const rowId = get(payload, 'rowId', null);
    const rowIndex = this.findItemIndexByRowId(rowId, payload.rowIndex);
    const items = [...this.getControlValue()];

    items[rowIndex] = Object.assign({}, payload.value);

    this.setControlValue(items);
  }

  public inlinePinnedTopCellEditComplete(
    payload: XuxGridAction['payload'],
  ): void {
    const controlValue = [...this.getControlValue()];
    this.displayedItemsIds = getDisplayedItemsIds(
      this.xuxGridComponent.gridApi as any,
    );

    const field = get(payload, 'field', '');
    const value = payload.value;
    const valueField = this.gridOptions.valueField;
    const items = map(controlValue, (item) => {
      const skipUpdating =
        (item?.widgetData?.hideCellRendererIfEmptyValue ?? false) &&
        isUndefined(item.current);
      if (skipUpdating) return item;
      const updatedItem = this.skipControlsOnItemCloning(item);

      if (this.displayedItemsIds.includes(get(item, valueField))) {
        const itemMapper: Function = get(
          item,
          'widgetData.pinnedRowItemMapper',
        );

        return itemMapper
          ? itemMapper(updatedItem, value, field, set)
          : set(updatedItem, field, value);
      }

      return updatedItem;
    });

    // need set all grid row data here
    this.setControlValue(items, true);
  }

  public inlineCellEditComplete(payload: XuxGridAction['payload']): void {
    if (this.gridOptions.treeViewMode) {
      this.inlineEditTreeStructure(payload);
    } else {
      this.inlineEditFlatStructure(payload);
    }
  }

  public inlineEditTreeStructure(payload: XuxGridAction['payload']): void {
    const rowId = get(payload, 'rowId', null);
    const valueField = this.gridOptions.valueField;
    const treeChildrenPropertyName = this.gridOptions.treeChildrenPropertyName;
    const changedItem = this.xuxGridComponent.gridApi.getRowNode(rowId)?.data;
    const updatedRow = set(
      Object.assign({}, changedItem),
      payload.field,
      payload.value,
    );
    const beforeEditingMappers = get(
      this.widgetData,
      'inlineEditOptions.beforeActionMappers',
      [],
    );

    let updatedItems = this.xuxGridItems.map((item) => {
      let treeValue = get(item, treeChildrenPropertyName, []);

      if (item[valueField] === rowId) {
        return getFlowMethods(beforeEditingMappers)(
          { ...item, [payload.field]: payload.value },
          payload,
        );
      }
      if (!has(item, treeChildrenPropertyName)) return item;
      const setTreeValue = (treeValueArg: ITreeData[]): ITreeData[] => {
        return map(treeValueArg, (treeItem) => {
          if (!has(treeItem, treeChildrenPropertyName)) {
            return treeItem[valueField] === rowId
              ? getFlowMethods(beforeEditingMappers)(
                  set(
                    Object.assign({}, treeItem),
                    payload.field,
                    payload.value,
                  ),
                  payload,
                )
              : treeItem;
          }

          return {
            ...treeItem,
            [treeChildrenPropertyName]: setTreeValue(
              get(treeItem, treeChildrenPropertyName, {}),
            ),
          };
        });
      };
      treeValue = setTreeValue(treeValue);

      return { ...item, [treeChildrenPropertyName]: treeValue };
    });

    const beforeActionItemsMappers = get(
      this.widgetData,
      'inlineEditOptions.beforeActionItemsMapper',
      [],
    );

    if (!isEmpty(beforeActionItemsMappers)) {
      updatedItems = getFlowMethods(beforeActionItemsMappers)(
        updatedRow,
        payload,
        updatedItems,
      );
    }

    const updateOtherGridItemFunc = get(
      this.widgetData,
      'inlineEditOptions.updateOtherGridItemFunc',
      [],
    );

    if (isFunction(updateOtherGridItemFunc)) {
      forEach(updateOtherGridItemFunc(payload, updatedItems), (item) => {
        this.updateGridItemAndReOrder(item, true);
      });
    }

    const updatedRowAfterMappers = getFlowMethods(beforeEditingMappers)(
      updatedRow,
      payload,
    );
    this.setControlValue(updatedItems);
    this.updateGridItemAndReOrder(
      {
        ...payload,
        value: updatedRowAfterMappers,
      },
      true,
    );

    const afterEditingMappers = get(
      this.widgetData,
      'inlineEditOptions.afterActionMappers',
      [],
    );
    getFlowMethods(afterEditingMappers)(this, updatedRowAfterMappers, payload);
  }

  public inlineEditFlatStructure(payload: XuxGridAction['payload']): void {
    const rowId = get(payload, 'rowId', null);
    const rowIndex = this.findItemIndexByRowId(rowId, payload.rowIndex);
    const items = [...this.xuxGridItems];

    const changedItem = this.xuxGridComponent.gridApi.getRowNode(rowId)?.data;

    // if nothing changed - do nothing
    if (
      !this.ignoreItemValueOptimization &&
      isEqual(get(changedItem, payload.field), payload.value)
    ) {
      return;
    }

    const updatedRow = set(
      Object.assign({}, changedItem),
      payload.field,
      payload.value,
    );

    const beforeEditingMappers = get(
      this.widgetData,
      'inlineEditOptions.beforeActionMappers',
      [],
    );
    const beforeActionItemsMappers = get(
      this.widgetData,
      'inlineEditOptions.beforeActionItemsMapper',
      [],
    );
    // @ts-ignore
    const updatedRowAfterMappers = getFlowMethods(beforeEditingMappers)(
      updatedRow,
      payload,
    );

    const newRowValue = { ...changedItem, ...updatedRowAfterMappers };

    items[rowIndex] = newRowValue;
    if (!isEmpty(beforeActionItemsMappers)) {
      const updatedRowAfterMapperItems = getFlowMethods(
        beforeActionItemsMappers,
      )(updatedRow, payload);

      forEach(updatedRowAfterMapperItems, (item) => {
        const index = findIndex(items, { internal_grid_row_id: item.rowId });
        if (index > -1) {
          items[index] = item.value;
          this.updateGridItemAndReOrder(
            {
              field: item.field,
              rowId: item.rowId,
              value: item.value,
            },
            true,
          );
        }
      });
    }

    this.setControlValue(items);
    this.updateGridItemAndReOrder(
      {
        field: payload.field,
        rowId: payload.rowId,
        value: newRowValue,
      },
      true,
    );

    const afterEditingMappers = get(
      this.widgetData,
      'inlineEditOptions.afterActionMappers',
      [],
    );
    getFlowMethods(afterEditingMappers)(this, newRowValue, payload);
  }

  /**
   * create new item document and update items collection
   */
  public async createNewItem(rowIndex?: number): Promise<void> {
    const entityId = this.widgetData?.uniqEntityIdGenerator
      ? this.widgetData.uniqEntityIdGenerator()
      : `local${generateId()}`;
    const value = merge({}, get(this.widgetData, 'defaultDocument'), {
      entity_id: entityId,
    });
    const title = this.composeModalTitle(value, this.modalTitlePrefixes.create);
    const options = this.getPopupOptions(value, title);
    const result = await this.uiActions.promptFormModal(options);

    if (!result.confirmed) {
      return;
    }

    const resultMappers = get(
      this.modalOptions.modalMappers,
      'edit.resultMappers',
      [],
    );

    result.value = getFlowMethods(resultMappers)(result.value);

    const confirmationOptions = await this.getConfirmationModalOptions(
      this.modalTitlePrefixes.create,
      [result.value],
    );

    if (confirmationOptions) {
      const modalDialogResult: IModalDialogResult =
        await this.uiActions.confirm(confirmationOptions);

      if (!modalDialogResult.confirmed) {
        return;
      }
    }

    const createdItem = this.removeUniqIdentifier(result.value);
    // By default, if there is no "rowIndex" data, then a created item will be added to the end of an array.
    // If "rowIndex" is enabled, then a new item will be added before the position "rowIndex".
    // If we need to add an item after a selected position, then the parameter should be incremented before calling.
    const items = [...this.getControlValue()];
    const positionToInsert = isNumber(rowIndex) ? rowIndex : items.length;
    items.splice(positionToInsert, 0, createdItem);
    // update grid item data, will fire subscription to set grid value when items is empty
    if (!isEmpty(items)) {
      this.updateGridRowData({
        addIndex: positionToInsert,
        add: [createdItem],
      });
    }

    this.updateDataAfterCreationItem(result, items);
  }

  public updateDataAfterCreationItem(
    result: IModalDialogResult,
    items: any[],
  ): void {
    this.setControlValue(items);

    const emitData = {
      type: EDIT_GRID_ITEM_ACTION_TYPE.GRID_ITEM_ADDED,
      payload: result.value,
    };

    this.emitAction(emitData);
  }

  public async editItem(inputValue: object, rowIndex: number): Promise<void> {
    const beforeEditingMappers = get(
      this.modalOptions.modalMappers,
      'edit.beforeActionMappers',
      [],
    );
    // @ts-ignore
    const value = getFlowMethods(beforeEditingMappers)(inputValue);

    const title = this.composeModalTitle(value, this.modalTitlePrefixes.edit);
    const options = this.getPopupOptions(value, title);
    const result = await this.uiActions.promptFormModal(options);

    if (!result.confirmed) {
      return;
    }

    const resultMappers = get(
      this.modalOptions.modalMappers,
      'edit.resultMappers',
      [],
    );
    // @ts-ignore
    result.value = getFlowMethods(resultMappers)(result.value);

    const confirmationOptions = await this.getConfirmationModalOptions(
      this.modalTitlePrefixes.edit,
      [result.value],
    );

    if (confirmationOptions) {
      const modalDialogResult: IModalDialogResult =
        await this.uiActions.confirm(confirmationOptions);

      if (!modalDialogResult.confirmed) {
        return;
      }
    }

    const items = this.xuxGridItems.map((item) =>
      this.getItemValue(item) === this.getItemValue(value)
        ? result.value
        : item,
    );

    this.setControlValue(items);

    this.updateGridItemAndReOrder(
      {
        value: result.value,
        rowId:
          this.xuxGridComponent.gridApi.getDisplayedRowAtIndex(rowIndex)?.id,
      },
      true,
    );

    const emitData = {
      type: EDIT_GRID_ITEM_ACTION_TYPE.GRID_ITEM_EDITED,
      payload: result.value,
    };

    this.emitAction(emitData);
  }

  public moveItems(direction: string, selectedItems: object[]): void {
    const preparedSelectedItems = selectedItems?.concat();
    const controlValue = this.getControlValue();
    const [filteredSelectedItems, filteredItems] = partition(
      controlValue,
      (item) => find(preparedSelectedItems, item),
    );
    const items = moveItemsHelper[direction](
      filteredSelectedItems,
      filteredItems,
    );

    // update grid row data
    this.setGridRowData(items);
    this.xuxGridComponent.emitAction({
      type: XUX_GRID_ACTIONS.ITEM_ORDER_CHANGED,
      payload: {
        rowData: this.removeUniqIdentifier(items) as object[],
      },
    });
  }

  public composeModalTitle(value: object, action: string): string {
    const modalTitle = get(this, 'widgetData.modalTitle');
    const ActionsMapping = {
      [this.modalTitlePrefixes.create]: I18n.Common.Create,
      [this.modalTitlePrefixes.edit]: I18n.Common.Edit,
    };
    const translatedAction = this.i18nService.instant(ActionsMapping[action]);

    if (isFunction(modalTitle)) return modalTitle([value], translatedAction);

    return !isEmpty(modalTitle)
      ? `${translatedAction} ${this.i18nService.instant(modalTitle)}`
      : translatedAction;
  }

  public getPopupOptions(value: object, title?: string): IModalDialogOptions {
    const valueField = this.gridOptions.valueField;
    const formRules = this.modalOptions.formRules;
    const options = { ...this.modalOptions, value };
    const valueFieldValidator = (control): ValidationErrors => {
      // In edit mode we should not validate same value
      if (title && control.value === value[valueField]) return null;

      const gridValue = reject(get(this.field, 'control.value', []), {
        [valueField]: control.value,
      });

      const isItemAlreadyAdded = !!find(gridValue, [valueField, control.value]);

      return isItemAlreadyAdded
        ? {
            not_uniq: this.i18nService.instant(
              I18n.InfoMessages.SuchValueHasAlreadyBeenAdded,
            ),
          }
        : null;
    };
    const fieldValidatorsPathArray = ['properties', valueField, 'validators'];
    const fieldValidators = get(options.schema, fieldValidatorsPathArray, []);
    const newFieldValidators = [...fieldValidators, valueFieldValidator];

    if (title) options.title = title;
    if (!get(options.schema, ['properties', valueField])) {
      set(options.schema, ['properties', valueField, 'show'], false);
    }
    // Add uniq valueField validator
    set(options.schema, fieldValidatorsPathArray, newFieldValidators);

    // Validator that prevent adding the same row to grid.
    const uniqGridItemValidator = (control): ValidationErrors => {
      const gridValue = get(this.field, 'control.value', []);
      const gridValueWithoutOpenedItem = filter(
        gridValue,
        (item, index: number) => index !== this.currentItemIndex,
      );
      const controlValueToCompare = this.needToAddUniqIdentifier
        ? omit(control.value, INTERNAL_GRID_ROW_ID)
        : control.value;
      const isItemAlreadyAdded = some(
        gridValueWithoutOpenedItem,
        (gridItem) => {
          const gridItemToCompare = this.needToAddUniqIdentifier
            ? omit(gridItem, INTERNAL_GRID_ROW_ID)
            : gridItem;
          const fieldsForComparing = get(
            this.field,
            'widgetData.uniqGridItemValidatorCompareFields',
          );
          const getValueToCompare = (
            valueToCompare,
            fieldsToPick,
          ): { [key: string]: any } =>
            !isEmpty(fieldsToPick)
              ? pick(valueToCompare, fieldsToPick)
              : valueToCompare;
          const preparedGridItemToCompare = getValueToCompare(
            gridItemToCompare,
            fieldsForComparing,
          );
          const preparedControlValueToCompare = getValueToCompare(
            controlValueToCompare,
            fieldsForComparing,
          );

          return isEqual(
            preparedGridItemToCompare,
            preparedControlValueToCompare,
          );
        },
      );

      // In edit mode we should not validate same value
      if (title && isEqual(control.value, value)) return null;

      return isItemAlreadyAdded
        ? {
            not_uniq: this.i18nService.instant(
              I18n.ErrorMessages.UniquenessFieldCombinationError,
            ),
          }
        : null;
    };
    const popupFormValidators = get(options.schema, 'validators', []);
    const newPopupFormValidators = [
      ...popupFormValidators,
      uniqGridItemValidator,
    ];
    // Add uniq grid item validator
    set(options.schema, 'validators', newPopupFormValidators);

    set(options, 'formRules', formRules);
    set(options, 'subTitle', get(this.widgetData, 'modalSubTitle', ''));

    return options;
  }

  public async removeItems(
    itemsToRemove: object[] = [],
    noConfirm: boolean = false,
  ): Promise<void> {
    let confirmed = noConfirm;
    if (!confirmed) {
      const confirmationOptions = await this.getConfirmationModalOptions(
        'remove',
        itemsToRemove,
      );

      const modalDialogResult: IModalDialogResult =
        await this.uiActions.confirm(confirmationOptions);
      confirmed = modalDialogResult.confirmed;
    }

    if (confirmed) {
      const items: object[] = [...this.xuxGridItems];

      let updatedAndSortedItems = filter(
        items,
        (item) => !find(itemsToRemove, omit(item, INTERNAL_GRID_VALUE_SET)),
      );
      const isTreeMode = this.gridOptions.treeViewMode;

      if (isTreeMode) {
        const treeChildrenPropertyName = get(
          this.gridOptions,
          'treeChildrenPropertyName',
          '',
        );
        updatedAndSortedItems = updatedAndSortedItems
          .map((item) => {
            if (!has(item, treeChildrenPropertyName)) return item;
            const treeItems = get(item, treeChildrenPropertyName, []);
            const treeItemsFiltered = filter(
              treeItems,
              (treeItem) => !find(itemsToRemove, treeItem),
            );

            return treeItemsFiltered.length
              ? { ...item, [treeChildrenPropertyName]: treeItemsFiltered }
              : null;
          })
          .filter(Boolean);
      }

      // remove grid row data
      this.updateGridRowData({ remove: itemsToRemove });
      this.setControlValue(updatedAndSortedItems);

      this.refreshGridCells(head(keys(this.fieldChangeRefreshCellCondition)));

      const emitData = {
        type: EDIT_GRID_ITEM_ACTION_TYPE.GRID_ITEMS_REMOVED,
        payload: itemsToRemove,
      };

      this.emitAction(emitData);
    }
  }

  public handleHeaderButtonClick = (value: string): void => {
    this.xuxGridComponent.emitAction({
      type: XUX_GRID_ACTIONS.ACTION_BUTTON_CLICKED,
      payload: {
        value,
      },
    });
  };

  public resetGridRowHeights(): void {
    if (this.xuxGridComponent && this.xuxGridComponent.gridApi) {
      setTimeout(() => this.xuxGridComponent.gridApi.resetRowHeights(), 0);
    }
  }

  public updateGridItemAndReOrder(
    payload: XuxGridAction['payload'],
    forceUpdateWholeRow: boolean = false,
  ): void {
    const rowId = get(payload, 'rowId', null);
    const updatedItem = this.xuxGridComponent.gridApi.getRowNode(rowId);

    if (forceUpdateWholeRow || isEmpty(payload.field)) {
      updatedItem.setData(payload.value);
    } else {
      updatedItem.setDataValue(payload.field, payload.value);
    }

    this.xuxGridComponent.gridApi.redrawRows({ rowNodes: [updatedItem] });
    this.refreshGridCells(payload.field);
    // order again
    this.xuxGridComponent.gridApi.setSortModel(
      this.xuxGridComponent.gridApi.getSortModel(),
    );
    this.reFocusCell();
  }

  public focusCellInnerElement(event: CellFocusedEvent): void {
    const focusedCellRendererInstance = getCellRendererInstance({
      rowIndex: event.rowIndex,
      columnId: event.column.getColId(),
      gridApi: event.api,
    });

    if (
      !isNil(focusedCellRendererInstance) &&
      some(
        xviKeyboardAutoFocusGridComponents,
        (component) =>
          focusedCellRendererInstance['_frameworkComponentInstance'] instanceof
          component,
      )
    ) {
      const inputEl: HTMLElement = focusedCellRendererInstance
        .getGui()
        .querySelector('input');

      if (
        !isEqual(document.activeElement, inputEl) &&
        !isEqual(document.activeElement.parentElement, inputEl.parentElement)
      ) {
        inputEl.focus();
      }
    }
  }

  protected get curFocusedCell(): CellPosition {
    return this.xuxGridComponent.gridApi.getFocusedCell();
  }

  /**
   * refresh grid data
   * when `refreshCellCondition` is ALWAYS_REFRESH or updateField is true in `refreshCellCondition`
   *
   * @param updateField update field
   * @returns
   */
  protected refreshGridCells(updateField: string = ''): void {
    const alwaysRefresh = get(
      this.fieldChangeRefreshCellCondition,
      REFRESH_CELLS_CONDITIONS.ALWAYS_REFRESH,
      false,
    );
    if (isEmpty(updateField) && !alwaysRefresh) {
      return;
    }

    if (
      alwaysRefresh ||
      get(this.fieldChangeRefreshCellCondition, updateField, false)
    ) {
      this.xuxGridComponent.gridApi.refreshCells({ force: true });
    }
  }

  protected updateGridRowData(rowDataTransaction?: RowDataTransaction): void {
    this.xuxGridComponent.gridApi.updateRowData(rowDataTransaction);
  }

  protected setGridRowData(rowData: object[] = this.xuxGridItems): void {
    if (!this.xuxGridComponent.gridApi) {
      return;
    }

    this.xuxGridComponent.gridApi.setRowData(rowData);
    this.xuxGridComponent.gridApi.redrawRows();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected updateInnerXuxGridItems(items: object[]): void {
    // emit xuxGrid array changed action
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED,
      payload: { rowData: this.xuxGridItems },
    };

    this.xuxGridComponent.emitAction(gridAction);
  }

  protected suppressKeyboardEvent(
    params: SuppressKeyboardEventParams,
  ): boolean {
    if (!includes(GRID_NAVIGATION_AVAILABLE_KEYS, params.event.key)) {
      return true;
    }
    if (isEqual(params.event.key, KEY_CODES.ENTER)) {
      params.event.preventDefault(); // to prevent second call for this event
      queueMicrotask(() => {
        const inputEl: HTMLInputElement = (
          params.event.target as HTMLElement
        ).querySelector('input');
        if (!isNil(inputEl)) {
          const customGridFocusEvent = new Event('gridCustomFocus', {
            bubbles: false,
          });
          inputEl.dispatchEvent(customGridFocusEvent);
        }
      });

      return true;
    }

    return false;
  }

  protected onRowDragMove(rowEvent: RowDragEvent): void {
    const movingNode = rowEvent.node;
    const overNode = rowEvent.overNode;

    const parentMovingNode = get(movingNode, 'parent', null);
    const parentOverNode = get(overNode, 'parent', null);
    const isTreeChildrenItem =
      has(parentMovingNode, 'data') && has(parentOverNode, 'data');

    if (isTreeChildrenItem) {
      return;
    }

    // only support origin order drag
    if (movingNode !== overNode) {
      const fromIndex = get(movingNode, 'childIndex', -1);
      const toIndex = get(overNode, 'childIndex', -1);
      if (!isValidNumber(fromIndex) || !isValidNumber(toIndex)) {
        return;
      }
      const newRowData = this.xuxGridItems.concat();
      // reorder items array
      [newRowData[fromIndex], newRowData[toIndex]] = [
        newRowData[toIndex],
        newRowData[fromIndex],
      ];

      this.xuxGridItems = newRowData;

      if (!get(this.gridOptions, 'deltaRowDataMode')) {
        this.xuxGridComponent.gridApi.clearFocusedCell();
      }
    }
  }

  protected onRowDragEnd(rowEvent: RowDragEvent): void {
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.ITEM_ORDER_CHANGED,
      payload: {
        rowData: this.xuxGridItems,
        value: rowEvent,
      },
    };
    this.xuxGridComponent.emitAction(gridAction);
  }

  protected onCellFocused(event: CellFocusedEvent): void {
    if (isNil(event.column)) return;

    const isSameAsFocusedGridContext =
      this.agGridService.isSameAsFocusedGridContext(event.api['context']);

    if (isSameAsFocusedGridContext || event.api?.getFocusedCell()) {
      // need this for inventory count page(async update)
      if (this.gridOptions.focusCellInnerElWithDebounce) {
        this.debounceFocusCellInnerElement(event);
      } else {
        this.focusCellInnerElement(event);
      }
    }

    if (isSameAsFocusedGridContext) {
      return;
    }

    const gridApi = this.agGridService.focusedGridContext.getBean('gridApi');
    clearFocusedCellRenderer({ gridApi });
    gridApi.clearFocusedCell();
    this.agGridService.updateFocusedGridContext(event.api['context']);
  }

  protected onCellMouseDown(): void {
    // It takes a while to calculate the next column that can be focused,
    // need to wait for a while
    setTimeout(() => {
      const inputEl: HTMLInputElement = document.querySelector(
        '.ag-cell-focus input:not(.ignore-focus)',
      );
      if (!isNil(inputEl)) {
        inputEl.focus();
      }
    });
  }

  protected onCellEditingStopped(): boolean {
    /*
     A deferred check of existing of editable cells in a grid.
     If there is no cell in edit mode the 'ITEMS_DATA_CHANGED' action will fire.
     */
    setTimeout(() => {
      if (!this.xuxGridComponent.isCellEditInProgress) {
        const gridAction: XuxGridAction = {
          type: XUX_GRID_ACTIONS.ITEMS_DATA_CHANGED,
          payload: { rowData: this.xuxGridItems },
        };

        this.xuxGridComponent.emitAction(gridAction);
      }
    }, 0);

    /*
     switching this flag to a 'false' state should always be synchronous
     it means that all needed operations on editable cell were completed
     */
    return (this.xuxGridComponent.isCellEditInProgress = false);
  }

  protected navigateToNextEditableCell(
    params: NavigateToNextCellParams,
  ): CellPosition {
    // default is current position
    if (isNil(params.nextCellPosition)) {
      return this.curFocusedCell;
    }
    // Fixed a console error when grid has pinned bottom row(just return cur focused cell)
    if (!isNil(params.nextCellPosition?.rowPinned)) {
      return this.curFocusedCell;
    }
    if (
      includes([KEY_CODES.ARROW_UP, KEY_CODES.ARROW_DOWN], params.event.key)
    ) {
      if (
        !this.gridOptions.alwaysFindNextEditCell &&
        !this.gridOptions.treeViewMode
      ) {
        return params.nextCellPosition;
      }

      return this.getNextAvailableRowPosition(
        params.nextCellPosition,
        params.event.key,
      );
    }
    if (
      this.isEditableColumn(
        params.nextCellPosition.column,
        params.nextCellPosition.rowIndex,
      )
    ) {
      return params.nextCellPosition;
    }
    let allColumns = this.xuxGridComponent.columnApi.getColumnState();
    if (isEqual(KEY_CODES.ARROW_LEFT, params.event.key)) {
      allColumns = reverse(allColumns);
    }

    return this.getNextEditableCellPosition(allColumns, params);
  }

  protected tabToNextEditableCell(params: TabToNextCellParams): CellPosition {
    if (isNil(params.nextCellPosition)) {
      return params.nextCellPosition;
    }
    if (
      this.isEditableColumn(
        params.nextCellPosition.column,
        params.nextCellPosition.rowIndex,
      )
    ) {
      return params.nextCellPosition;
    }
    const allColumns = this.xuxGridComponent.columnApi.getColumnState();

    return this.getNextEditableCellPosition(
      allColumns,
      params,
      true,
      params.backwards,
    );
  }

  protected addGridRowData(value: object, addIndex?: number): void {
    this.xuxGridItems = this.xuxGridItems.concat({
      ...value,
    });

    this.setValue([...this.xuxGridItems]);
    this.updateGridRowData({ addIndex, add: [value] });
  }

  /**
   * setFieldControlValue
   *
   * @param data grid control data
   * @param isNeedUpdateXuxItems is need update grid all row data
   */
  protected setFieldControlValue(
    data: object[],
    isNeedUpdateGridRowData = false,
  ): void {
    this.field.control.setValue(
      isNeedUpdateGridRowData ? data : this.addInternalSetFlagToHead(data),
    );
  }

  /**
   * add INTERNAL_GRID_VALUE_SET field to array
   *
   * @param data target array
   * @returns new array with add head element INTERNAL_GRID_VALUE_SET field
   */
  protected addInternalSetFlagToHead(data: object[]): object[] {
    if (isEmpty(data)) return data;

    return concat(
      [{ ...head(data), [INTERNAL_GRID_VALUE_SET]: true }],
      tail(data),
    );
  }

  /**
   * remove INTERNAL_GRID_VALUE_SET field of array
   *
   * @param data target array
   * @returns new array with remove head element INTERNAL_GRID_VALUE_SET field
   */
  protected removeInternalSetFlagOfHead(data: object[]): object[] {
    if (isEmpty(data)) return data;

    return concat([omit(head(data), INTERNAL_GRID_VALUE_SET)], tail(data));
  }

  public getListViewData(): object[] {
    const controlValue: ITreeData[] | object[] = this.getControlValue();

    return flatTreeDataByHasChildren(controlValue);
  }

  private initEditGridWidgetSubscription(): void {
    this.subscriptions.push(
      this.field.control.valueChanges.subscribe((items) =>
        this.updateGridState(items),
      ),
    );

    if (this.isAutoHeightMode() && this.isGridRowLoader$) {
      this.subscriptions.push(
        this.isGridRowLoader$
          .pipe(
            rx.filter((state) => !state),
            rx.debounceTime(DEFAULT_FORM_UPDATE_DELAY_MS),
          )
          .subscribe(() => this.resetGridRowHeights()),
      );
    }
  }

  private isAutoHeightMode(): boolean {
    const columns: IXVIListColumn[] = this.widgetData?.columns ?? [];

    return some(columns, (column) => column.autoHeight);
  }

  private skipControlsOnItemCloning(item: IBaseEntityType): IBaseEntityType {
    let updatedItem;

    if (has(item, 'widgetData')) {
      updatedItem = cloneDeep(omit(item, 'widgetData'));
      set(
        updatedItem,
        'widgetData',
        prepareControlsInsideDataForCloning(item.widgetData),
      );
    } else {
      updatedItem = cloneDeep(item);
    }

    return updatedItem;
  }

  private getControlValue(): any[] {
    return this.field.control.value || [];
  }

  private setControlValue(
    items: object[],
    isNeedUpdateGridRowData: boolean = false,
  ): void {
    this.setFieldControlValue(items, isNeedUpdateGridRowData);
    this.field.control.markAsDirty();
    if (this.xuxGridComponent.gridApi) {
      this.updateActionButtons(this.xuxGridComponent.gridApi.getSelectedRows());
    }
  }

  private updateSchemaSubscription(schemaObs: Observable<SchemaNode>): void {
    if (this.schemaSubscription) {
      this.schemaSubscription.unsubscribe();
    }
    this.schemaSubscription = schemaObs.subscribe(
      (schema) => (this.modalOptions.schema = schema),
    );
  }

  private getItemValue(item: object): string {
    const valueField = this.gridOptions.valueField;

    return get(item, valueField, '');
  }

  private isSelectedFirstItem(
    allItems: object[],
    selectedItems: object[],
  ): boolean {
    return !!find(selectedItems, head(allItems));
  }

  private isSelectedLastItem(
    allItems: object[],
    selectedItems: object[],
  ): boolean {
    return !!find(selectedItems, last(allItems));
  }

  private getSelectedItemsRowIndexes(): number[] {
    let rowIndexes = [];
    if (this.xuxGridComponent.gridApi) {
      const selectedNodes = this.xuxGridComponent.gridApi.getSelectedNodes();
      rowIndexes = map(selectedNodes, 'rowIndex');
    }

    return rowIndexes;
  }

  private updateActionButtons(selectedItems: object[]): void {
    const singleItemSelected = get(selectedItems, 'length') === 1;
    const allItems = this.getControlValue();
    const selectedAllItems =
      get(selectedItems, 'length') === get(allItems, 'length');
    const isSelectedFirstItem = this.isSelectedFirstItem(
      allItems,
      selectedItems,
    );
    const isSelectedLastItem = this.isSelectedLastItem(allItems, selectedItems);
    const hasSelection = !isEmpty(selectedItems);

    let actionButtons = XuxGridActionButtonsHelper.copyButtons(
      this.availableActionButtons,
    );

    if (!this.widgetData.canAdd || this.isMaxItemsCountReached) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_CREATE_BTN.value },
        true,
      );
    }

    if (
      !this.widgetData.canAdd ||
      !this.widgetData.canAddInPosition ||
      !singleItemSelected
    ) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_INSERT_AFTER.value },
        true,
      );
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_INSERT_BEFORE.value },
        true,
      );
    }

    XuxGridActionButtonsHelper.manageEditActionButton(
      actionButtons,
      this.widgetData,
      selectedItems,
    );
    XuxGridActionButtonsHelper.manageRemoveActionButton(
      actionButtons,
      this.widgetData,
      selectedItems,
    );
    if (
      !this.advancedOrderControls ||
      this.shouldHideMoveButton(
        hasSelection,
        selectedAllItems,
        isSelectedLastItem,
      )
    ) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_MOVE_TO_BOTTOM.value },
        true,
      );
    }

    if (
      !this.advancedOrderControls ||
      this.shouldHideMoveButton(
        hasSelection,
        selectedAllItems,
        isSelectedFirstItem,
      )
    ) {
      XuxGridActionButtonsHelper.setHiddenValueForButton(
        actionButtons,
        { value: BUTTON_TEMPLATES.GRID_MOVE_TO_TOP.value },
        true,
      );
    }

    actionButtons = XuxGridActionButtonsHelper.applyCriteriaForActionButtons(
      actionButtons,
      selectedItems,
      this.disabled,
    );

    this.actionButtons = actionButtons;

    this.headerButtons = XuxGridActionButtonsHelper.getButtonsByPosition(
      actionButtons,
      TOOLBAR_BUTTON_POSITION.HEADER,
    );
    this.rowButtons = XuxGridActionButtonsHelper.getButtonsByPosition(
      actionButtons,
      TOOLBAR_BUTTON_POSITION.ROW,
    );
    this.gridButtons = XuxGridActionButtonsHelper.getButtonsByPosition(
      actionButtons,
      TOOLBAR_BUTTON_POSITION.DEFAULT,
    );
  }

  private updateErrorState(): void {
    if (!this.field?.control) {
      return;
    }

    const previousErrors: ValidationErrors | null = this.errorState;
    const controlErrors = this.field.control.errors;

    const errors = this.normalizeErrorRowClass(
      isEmpty(controlErrors) ? null : controlErrors,
    );
    const isErrorsUnchanged = isEqual(previousErrors, errors);

    if (isErrorsUnchanged) {
      return;
    }

    this.errorState = errors;
    this.syncErrorAndWarningToGridApi();
  }

  private updateWarningState(): void {
    if (!this.field?.control) {
      return;
    }
    const previousWarnings: ValidationErrors | null = this.warningState;
    const warningValidators: GridValidatorFn[] = get(
      this.widgetData,
      'warningValidators',
      [],
    );

    if (isEmpty(warningValidators)) {
      this.warningState = null;
      this.warningLabels = [];

      if (!isEqual(previousWarnings, null)) {
        this.syncErrorAndWarningToGridApi();
      }

      return;
    }

    const warningResult: ValidationErrors = chain(warningValidators)
      .filter(isFunction)
      .map((validator) => validator(this.field.control, this.xuxGridItems))
      .filter((result) => !isEmpty(result))
      .reduce(
        (result, currentWarning) =>
          this.mergeWarningValidatorResult(result, currentWarning),
        {},
      )
      .value();

    const warnings = this.normalizeWarningRowClass(
      isEmpty(warningResult) ? null : warningResult,
    );
    const nextWarningLabels = this.buildWarningLabels(warnings);
    const isWarningsUnchanged = isEqual(previousWarnings, warnings);
    const isWarningLabelsUnchanged = isEqual(
      this.warningLabels,
      nextWarningLabels,
    );

    if (isWarningsUnchanged && isWarningLabelsUnchanged) {
      return;
    }

    if (!isWarningsUnchanged) {
      this.warningState = warnings;
      this.syncErrorAndWarningToGridApi();
    }

    this.warningLabels = nextWarningLabels;
  }

  private normalizeErrorRowClass(
    errors: ValidationErrors | null,
  ): ValidationErrors | null {
    return this.normalizeValidationRowClass(errors, GridRowClass.ERROR_ROW);
  }

  private normalizeWarningRowClass(
    warnings: ValidationErrors | null,
  ): ValidationErrors | null {
    return this.normalizeValidationRowClass(warnings, GridRowClass.WARNING_ROW);
  }

  private normalizeValidationRowClass(
    validationResult: ValidationErrors | null,
    defaultRowClass: GridRowClass,
  ): ValidationErrors | null {
    if (isEmpty(validationResult)) {
      return null;
    }

    const normalizedValidationResult: ValidationErrors = {
      ...validationResult,
    };

    forEach(keys(normalizedValidationResult), (validationKey) => {
      const validationItem = get(normalizedValidationResult, validationKey);

      if (!isPlainObject(validationItem)) {
        return;
      }

      const validationRows = get(validationItem, 'data', []);
      const hasRows = isArray(validationRows) && !isEmpty(validationRows);

      if (!hasRows) {
        return;
      }

      // Only apply default rowClass if enableDefaultValidationRowClass is true.
      // Otherwise respect validator-provided rowClass or apply no style.
      const currentRowClass = get(validationItem, 'rowClass');
      const enableDefaultRowClass = get(
        validationItem,
        'enableDefaultValidationRowClass',
        false,
      );

      if (isEmpty(currentRowClass) && enableDefaultRowClass) {
        set(validationItem, 'rowClass', defaultRowClass);
      }

      // Clean up the configuration field after processing
      delete validationItem.enableDefaultValidationRowClass;
    });

    return normalizedValidationResult;
  }

  private mergeWarningValidatorResult(
    existingWarnings: ValidationErrors,
    incomingWarnings: ValidationErrors,
  ): ValidationErrors {
    const mergedWarnings: ValidationErrors = { ...existingWarnings };

    chain(incomingWarnings)
      .keys()
      .forEach((warningKey) => {
        const existingWarning = get(mergedWarnings, warningKey, {});
        const incomingWarning = get(incomingWarnings, warningKey, {});

        if (!isPlainObject(incomingWarning)) {
          set(mergedWarnings, warningKey, incomingWarning);

          return;
        }

        // Validate that both data fields are arrays before merging
        const existingData = get(existingWarning, 'data', []);
        const incomingData = get(incomingWarning, 'data', []);

        const isValidDataArray = isArray(existingData) && isArray(incomingData);

        if (!isValidDataArray) {
          // Data type mismatch, use incoming or gracefully degrade
          set(mergedWarnings, warningKey, incomingWarning);

          return;
        }

        const mergedData = uniq(compact([...existingData, ...incomingData]));

        set(mergedWarnings, warningKey, {
          ...existingWarning,
          ...incomingWarning,
          data: mergedData,
          count: mergedData.length,
        });
      })
      .value();

    return mergedWarnings;
  }

  private syncErrorAndWarningToGridApi(): void {
    const warnings = this.warningState;
    const errors = this.errorState;
    const gridApi = this.xuxGridComponent?.gridApi;

    if (!gridApi) {
      return;
    }

    const context = get(gridApi, [
      'gridOptionsWrapper',
      'gridOptions',
      'context',
    ]);

    // Collect entity IDs that were previously highlighted so we know which
    // rows to redraw after the context is updated.
    const prevValidationRowsByClass = get(
      context,
      'xviValidationRowsByClass',
      {},
    );
    const newValidationRowsByClass = this.mergeValidationRowsByClass(
      this.buildValidationRowsByClass(warnings),
      this.buildValidationRowsByClass(errors),
    );
    const isValidationContextUnchanged = isEqual(
      prevValidationRowsByClass,
      newValidationRowsByClass,
    );

    if (isValidationContextUnchanged) {
      return;
    }

    if (context) {
      // Store validation row membership in a single context payload so row
      // class evaluation and redraws use the same source of truth.
      set(context, 'xviValidationRowsByClass', newValidationRowsByClass);
    }

    // Only redraw rows whose class membership actually changed, to avoid
    // redrawing the entire grid on every inline-edit keystroke.
    const prevEntityIds = uniq([
      ...compact(Object.values(prevValidationRowsByClass).flat() as string[]),
    ]);
    const newEntityIds = uniq([
      ...compact(Object.values(newValidationRowsByClass).flat() as string[]),
    ]);
    const affectedEntityIds = uniq([...prevEntityIds, ...newEntityIds]);

    if (isEmpty(affectedEntityIds)) {
      return;
    }

    const rowNodesToRedraw = compact(
      affectedEntityIds.map((entityId) => gridApi.getRowNode(entityId)),
    );

    if (!isEmpty(rowNodesToRedraw)) {
      gridApi.redrawRows({ rowNodes: rowNodesToRedraw });

      return;
    }

    // Fallback for cases where row IDs are not resolvable yet (or differ from
    // entity_id): find currently rendered rows by data.entity_id and redraw
    // only those rows first.
    const affectedEntityIdSet = new Set(affectedEntityIds);
    const displayedRowNodesToRedraw: RowNode[] = [];

    gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
      const rowEntityId = get(rowNode, ['data', 'entity_id']);

      if (affectedEntityIdSet.has(rowEntityId)) {
        displayedRowNodesToRedraw.push(rowNode);
      }
    });

    if (!isEmpty(displayedRowNodesToRedraw)) {
      gridApi.redrawRows({ rowNodes: displayedRowNodesToRedraw });

      return;
    }

    // Final fallback: force full redraw to guarantee getRowClass reevaluation.
    gridApi.redrawRows();
  }

  private installValidationGetRowClass(): void {
    const gridApi = this.xuxGridComponent?.gridApi;

    if (!gridApi) {
      return;
    }

    const gridOptions = get(gridApi, ['gridOptionsWrapper', 'gridOptions']);

    if (!gridOptions) {
      return;
    }

    const currentGetRowClass = get(gridOptions, 'getRowClass');
    const installedGetRowClass =
      this.validationGetRowClassByGridOptions.get(gridOptions);

    if (installedGetRowClass && currentGetRowClass === installedGetRowClass) {
      return;
    }

    const baseGetRowClass = currentGetRowClass;
    const validationGetRowClass = (params): string | string[] => {
      const validationRowsByClass = get(
        params,
        ['context', 'xviValidationRowsByClass'],
        {},
      );
      const allValidationClasses = keys(validationRowsByClass);

      const rowEntityId = get(params, ['data', 'entity_id']);
      const validationClasses = compact(
        map(allValidationClasses, (validationClassName) => {
          const validationRows = get(
            params,
            ['context', 'xviValidationRowsByClass', validationClassName],
            [],
          );

          return includes(validationRows, rowEntityId)
            ? validationClassName
            : null;
        }),
      );

      const baseClasses = isFunction(baseGetRowClass)
        ? baseGetRowClass(params)
        : [];
      const normalizedBaseClasses = compact(
        isArray(baseClasses) ? baseClasses : [baseClasses],
      );

      return uniq(compact([...normalizedBaseClasses, ...validationClasses]));
    };

    set(gridOptions, 'getRowClass', validationGetRowClass);
    this.validationGetRowClassByGridOptions.set(
      gridOptions,
      validationGetRowClass,
    );
  }

  private buildValidationRowsByClass(
    validationResult: ValidationErrors | null,
  ): Record<string, string[]> {
    if (isEmpty(validationResult)) {
      return {};
    }

    // Convert validation results into a rowClass -> entity_id[] index that can
    // be consumed cheaply by ag-grid rowClassRules.
    return chain(validationResult)
      .keys()
      .reduce((rowsByClass, validationKey) => {
        const validationItem = get(validationResult, validationKey, {});

        if (!isPlainObject(validationItem)) {
          return rowsByClass;
        }

        const validationClassName = get(validationItem, 'rowClass');
        const validationRows = get(validationItem, 'data', []);

        if (
          isEmpty(validationClassName) ||
          !isArray(validationRows) ||
          isEmpty(validationRows)
        ) {
          return rowsByClass;
        }

        rowsByClass[validationClassName] = uniq(
          compact([
            ...(rowsByClass[validationClassName] || []),
            ...validationRows,
          ]),
        );

        return rowsByClass;
      }, {})
      .value();
  }

  private mergeValidationRowsByClass(
    ...rowsByClassList: Array<Record<string, string[]>>
  ): Record<string, string[]> {
    return chain(rowsByClassList)
      .filter((rowsByClass) => !isEmpty(rowsByClass))
      .reduce((mergedRowsByClass, rowsByClass) => {
        chain(rowsByClass)
          .keys()
          .forEach((validationClassName) => {
            const existingRows = get(
              mergedRowsByClass,
              validationClassName,
              [],
            );
            const incomingRows = get(rowsByClass, validationClassName, []);

            set(
              mergedRowsByClass,
              validationClassName,
              uniq(compact([...existingRows, ...incomingRows])),
            );
          })
          .value();

        return mergedRowsByClass;
      }, {})
      .value();
  }

  private buildWarningLabels(
    warnings: ValidationErrors | null,
  ): ILabelBlockItem[] {
    if (isEmpty(warnings)) {
      return [];
    }

    return chain(warnings)
      .keys()
      .map((warningKey) => {
        const message = get(warnings, [warningKey, 'message']);
        const messageParams = get(warnings, [warningKey, 'messageParams'], {});

        if (isEmpty(message)) {
          return null;
        }

        const translatedMessage = this.i18nService.instant(
          message,
          messageParams,
        );
        const widgetData = get(warnings, [warningKey, 'widgetData'], {});
        const itemClass = get(
          widgetData,
          'itemClass',
          get(warnings, [warningKey, 'class'], COMMON_WARN_MESSAGE_CLASS),
        );

        return {
          message: translatedMessage,
          show: true,
          class: itemClass,
          widgetData: {
            ...widgetData,
            itemClass,
          },
        };
      })
      .compact()
      .value();
  }

  private shouldHideMoveButton(
    hasSelection: boolean,
    selectedAllItems: boolean,
    isSelectedItemOnSomePosition: boolean,
  ): boolean {
    return (
      !hasSelection ||
      selectedAllItems ||
      (isSelectedItemOnSomePosition && this.itemsSelectedOneByOne())
    );
  }

  // TODO: Research possible ways to update pinned renderer automatically
  //       without this method in the future ag-grid versions
  private refreshPinnedRowCellRenderer(): void {
    if (!this.xuxGridComponent.gridApi) return;

    const pinnedRowNode = this.xuxGridComponent.gridApi.getPinnedTopRow(0);
    if (!pinnedRowNode) return;

    // animation of cell renderer occurs before the value is updated
    setTimeout(
      () =>
        this.xuxGridComponent.gridApi.refreshCells({
          force: true,
          rowNodes: [pinnedRowNode],
        }),
      0,
    );
  }

  private preventTabEventHandler(event: KeyboardEvent): void {
    if (isEqual(event.key, KEY_CODES.TAB)) {
      event.preventDefault();
    }
  }

  private preventTabEvent(): void {
    forEach(
      Array.from(document.querySelectorAll('.ag-dm')),
      (element: HTMLElement) => {
        element.addEventListener(
          'keydown',
          this.preventTabEventHandler.bind(this),
        );
      },
    );
  }

  private restoreTabEvent(): void {
    forEach(
      Array.from(document.querySelectorAll('.ag-dm')),
      (element: HTMLElement) => {
        element.removeEventListener(
          'keydown',
          this.preventTabEventHandler.bind(this),
        );
      },
    );
  }

  /**
   * check if exist gaps between selected items
   */
  private itemsSelectedOneByOne(): boolean {
    const rowIndexes = sortBy(this.getSelectedItemsRowIndexes());
    const firstIndex = head(rowIndexes);
    const lastIndex = last(rowIndexes);

    // +1 is because enumeration of array starts from 0.
    // a=[2,3,5];  5-2=3; a.length === 3+1; result is false;
    return rowIndexes.length === lastIndex - firstIndex + 1;
  }

  private prepareActionButtons(): void {
    this.availableActionButtons =
      XuxGridActionButtonsHelper.prepareActionButtons(this.widgetData);
  }

  private findItemIndexByRowId(rowId: string, defaultIndex?: number): number {
    const valueField = this.gridOptions.valueField;
    const itemIndexByRowId = findIndex(this.xuxGridItems, [valueField, rowId]);

    return itemIndexByRowId < 0 && isNumber(defaultIndex)
      ? defaultIndex
      : itemIndexByRowId;
  }

  private emitAction(data: any): void {
    const dataWithPath = {
      ...data,
      fieldPath: get(this, 'field.path'),
    };

    this.field.action.next(dataWithPath);
  }

  private async getConfirmationModalOptions(
    action: ModalAction,
    items: any[],
  ): Promise<IModalDialogOptions> {
    const customOptions =
      isFunction(this.widgetData.composeConfirmationModalOptions) &&
      (await (this.widgetData.composeConfirmationModalOptions(
        action,
        items,
      ) as Promise<any>));

    if (['create', 'edit'].includes(action) && customOptions) {
      return customOptions;
    }

    if (action === 'remove') {
      let commonName: string = get(this.widgetData, 'commonName', 'item');

      if (COMMON_NAME_CONFIG[commonName]) {
        commonName = gt(items.length, 1)
          ? COMMON_NAME_CONFIG[commonName].plural
          : COMMON_NAME_CONFIG[commonName].singular;
      }

      let removableItemsNames: string = compact(
        map(items, this.gridOptions.orderGhostLabelPath),
      ).join(', ');
      removableItemsNames = isEmpty(removableItemsNames)
        ? ''
        : `'${removableItemsNames}' `;
      const getConfirmationModalMessage: IModalDialogOptions = {
        text: this.i18nService.instant(
          I18n.ConfirmMessages.AreYouSureYouWantToRemoveItem,
          {
            removableItemsNames,
            commonName: this.i18nService.instant(commonName),
          },
        ),
      };

      return customOptions || getConfirmationModalMessage;
    }
  }

  private toggleCreateButton(): void {
    XuxGridActionButtonsHelper.setHiddenValueForButton(
      this.actionButtons,
      { value: BUTTON_TEMPLATES.GRID_CREATE_BTN.value },
      this.widgetData.canAdd && this.isMaxItemsCountReached,
    );

    this.gridOptions.inlineAdd =
      this.widgetData.inlineAdd && !this.isMaxItemsCountReached;
  }

  private handleActionButtonClicked(payload): void {
    const rowData = payload.rowData;
    const selectedItemRowIndex = head(this.getSelectedItemsRowIndexes());
    this.currentItemIndex = this.getCurrentItemIndex(payload.rowIndex);

    switch (payload.value) {
      case BUTTON_ACTIONS.CREATE:
        this.createNewItem();
        break;
      case BUTTON_ACTIONS.ADD_BEFORE:
        this.createNewItem(selectedItemRowIndex);
        break;
      case BUTTON_ACTIONS.ADD_AFTER:
        this.createNewItem(selectedItemRowIndex + 1);
        break;
      case BUTTON_ACTIONS.EDIT:
        this.editItem(head(rowData), this.currentItemIndex);
        break;
      case BUTTON_ACTIONS.REMOVE:
        this.removeItems(rowData);
        break;
      case BUTTON_ACTIONS.MOVE_TO_TOP:
      case BUTTON_ACTIONS.MOVE_TO_BOTTOM:
        this.moveItems(payload.value, rowData);
        break;
      default:
        break;
    }
  }

  private async customButtonHandler(
    customButtonHandler: Function,
  ): Promise<void> {
    const value = await customButtonHandler();

    if (!value) return;

    this.addGridRowData(value);
  }

  private setDisabledInlineAddBtnObs(): void {
    if (!isFunction(this.widgetData.inlineAddBtnDisabledHandler)) return;
    this.disabledInlineAddBtn$.next(
      this.widgetData.inlineAddBtnDisabledHandler(
        this.xuxGridItems,
        this.field?.control,
      ),
    );
  }

  private isEditableColumn(column: Column, nextRowIndex: number): boolean {
    const colDef = column.getColDef();
    const rowData = this.xuxGridComponent.gridApi
      .getModel()
      .getRow(nextRowIndex);
    if (!this.gridOptions.treeViewMode) {
      let isLeafRowFormGridOption = true;
      if (isFunction(this.gridOptions.isLeafRow)) {
        isLeafRowFormGridOption = this.gridOptions.isLeafRow(rowData?.data);
      }

      return (
        !!colDef?.editable && column.isVisible() && isLeafRowFormGridOption
      );
    }

    return !!colDef?.editable && column.isVisible() && !!rowData?.data.isLeaf;
  }

  private isLeafRow(data: IBaseEntityType): boolean {
    if (isFunction(this.gridOptions.isLeafRow)) {
      return this.gridOptions.isLeafRow(data);
    }

    return !has(data, this.gridOptions.treeChildrenPropertyName);
  }

  private getPreviousAvailableRowIdx(
    prevRowIdx: number,
    isJumpToEditable: boolean = false,
  ): number {
    let previousAvailableRowIdx = prevRowIdx;

    if (!this.gridOptions.treeViewMode && prevRowIdx > 0 && !isJumpToEditable) {
      return prevRowIdx - 1;
    }

    for (let i = prevRowIdx - 1; i >= 0; i -= 1) {
      const rowData = this.xuxGridComponent.gridApi.getModel().getRow(i);
      if (
        !!rowData?.data.isLeaf ||
        (!!rowData?.data.isEdit && isJumpToEditable)
      ) {
        previousAvailableRowIdx = i;
        break;
      }
    }
    if (isJumpToEditable && isEqual(previousAvailableRowIdx, prevRowIdx)) {
      return null;
    }

    return previousAvailableRowIdx;
  }

  private getNextAvailableRowIdx(
    nextRowIdx: number,
    rowCount: number,
    isJumpToEditable: boolean = false,
  ): number {
    let nextAvailableRowIdx = nextRowIdx;

    if (
      !this.gridOptions.alwaysFindNextEditCell &&
      !this.gridOptions.treeViewMode &&
      nextRowIdx < rowCount &&
      !isJumpToEditable
    ) {
      return nextRowIdx + 1;
    }

    for (let i = nextRowIdx + 1; i <= rowCount; i += 1) {
      const rowData = this.xuxGridComponent.gridApi.getModel().getRow(i);
      if (
        !!rowData?.data.isLeaf ||
        (!!rowData?.data.isEdit && isJumpToEditable)
      ) {
        nextAvailableRowIdx = i;
        break;
      }
    }

    if (isJumpToEditable && isEqual(nextAvailableRowIdx, nextRowIdx)) {
      return null;
    }

    return nextAvailableRowIdx;
  }

  private getNextEditableColumn(
    allColumns: ColumnState[],
    focusedCell: CellPosition,
  ): Column {
    if (isEmpty(allColumns)) return focusedCell.column;
    let currentColumnIdx = findIndex(
      allColumns,
      (columnState) => columnState.colId === focusedCell.column?.getColId(),
    );

    if (isEqual(currentColumnIdx, -1)) currentColumnIdx = 0;

    for (let idx = currentColumnIdx + 1; idx < allColumns.length; idx += 1) {
      const column = this.xuxGridComponent.columnApi.getColumn(
        allColumns[idx].colId,
      );
      if (this.isEditableColumn(column, focusedCell.rowIndex)) {
        return column;
      }
    }

    return focusedCell.column;
  }

  private getNextAvailableRowPosition(
    nextCellPosition: CellPosition,
    direction: string,
  ): CellPosition {
    const allRows: RowNode[] = [];
    let targetRowIdx = null;
    if (isNil(nextCellPosition)) return null;
    this.xuxGridComponent.gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
      allRows.push(rowNode);
    });
    if (isEqual(direction, KEY_CODES.ARROW_UP)) {
      for (let index = nextCellPosition.rowIndex; index >= 0; index -= 1) {
        if (this.isLeafRow(allRows[index].data)) {
          targetRowIdx = index;
          break;
        }
      }
    } else {
      for (
        let index = nextCellPosition.rowIndex;
        index < allRows.length;
        index += 1
      ) {
        if (this.isLeafRow(allRows[index].data)) {
          targetRowIdx = index;
          break;
        }
      }
    }

    if (isNil(targetRowIdx)) return this.curFocusedCell;

    return {
      ...nextCellPosition,
      rowIndex: targetRowIdx,
    };
  }

  private getNextEditableCellPosition(
    allColumns: ColumnState[],
    nextCellParams: NavigateToNextCellParams | TabToNextCellParams,
    isTabEvent = false,
    isBackwards = false,
  ): CellPosition {
    const allColumnsCopy = [...allColumns];
    if (isEmpty(allColumnsCopy)) return null;
    if (isBackwards) allColumnsCopy.reverse();

    let currentColumnIdx = findIndex(
      allColumnsCopy,
      (columnState) =>
        columnState.colId ===
        nextCellParams.nextCellPosition?.column?.getColId(),
    );

    if (isEqual(currentColumnIdx, -1)) currentColumnIdx = 0;

    const rowCount = this.xuxGridComponent.gridApi.getModel().getRowCount();
    let nextEditableColumn: Column = null;
    for (let idx = currentColumnIdx; idx < allColumnsCopy.length; idx += 1) {
      const column = this.xuxGridComponent.columnApi.getColumn(
        allColumnsCopy[idx].colId,
      );
      if (
        this.isEditableColumn(column, nextCellParams.nextCellPosition.rowIndex)
      ) {
        nextEditableColumn = column;
        break;
      }
    }

    if (!isNil(nextEditableColumn)) {
      return {
        rowIndex: nextCellParams.nextCellPosition.rowIndex,
        rowPinned: nextCellParams.nextCellPosition.rowPinned,
        column: nextEditableColumn,
      };
    }

    // we need return current focused cell when there is no other editable cell in the direction
    if (!isTabEvent) return this.curFocusedCell;

    if (
      isBackwards
        ? nextCellParams.nextCellPosition.rowIndex === 0
        : nextCellParams.nextCellPosition.rowIndex === rowCount - 1
    ) {
      // finally return
      return this.curFocusedCell;
    }

    // move index to next cell and judge is editable column
    const cellParams: NavigateToNextCellParams | TabToNextCellParams = {
      ...nextCellParams,
      nextCellPosition: {
        rowIndex: !isBackwards
          ? nextCellParams.nextCellPosition.rowIndex + 1
          : nextCellParams.nextCellPosition.rowIndex - 1,
        rowPinned: nextCellParams.nextCellPosition.rowPinned,
        column: this.xuxGridComponent.columnApi.getColumn(
          allColumnsCopy[0].colId,
        ),
      },
    };

    return this.getNextEditableCellPosition(
      allColumns,
      cellParams,
      isTabEvent,
      isBackwards,
    );
  }

  protected getWidgetDataDefaults(): IWidgetData {
    return this.field.widgetData;
  }

  // eslint-disable-next-line max-lines
}
